import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { DialogService } from 'primeng/dynamicdialog';
import {
  CropHealthViewModel,
  MapDistributionDataViewDto,
  MapDistributionView,
  SatelliteViewModel,
} from '@/app/proxy/proxies';
import * as echarts from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { PlantationService } from '@/app/core/services/plantation.service';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { NoAnalysisComponent } from '../no-analysis/no-analysis.component';
import { NdviNdmiDistributionColorLegendModalComponent } from './ndvi-ndmi-distribution-color-legend-modal/ndvi-ndmi-distribution-color-legend-modal.component';
import { CropHealthMapType } from '../crop-health/crop-health-map/crop-health-map-footer/crop-health-map-footer.component';

@Component({
  selector: 'app-ndvi-ndmi-distribution',
  imports: [SharedModule, NoAnalysisComponent],
  templateUrl: './ndvi-ndmi-distribution.component.html',
  styleUrl: './ndvi-ndmi-distribution.component.less',
  providers: [DialogService],
})
export class NdviNdmiDistributionComponent implements OnInit, OnChanges {
  @Input() loading = false;
  @Input() data: MapDistributionView[] = [];
  @Input() selectedDay: SatelliteViewModel;
  @Input() cropHealthData: CropHealthViewModel;
  @Input() selectedMapType: CropHealthMapType;
  /**
   * True only for the "No Analysis" card with the issue subtitle (not cloudy, not preparing).
   * Used for analytics only; does not affect UI/flow.
   */
  @Output() readonly noAnalysisIssueChanged = new EventEmitter<{ date: string; visible: boolean }>();

  activeTab: 'relative' | 'absolute' = 'relative';
  source: any[] = [];
  barChartOption: echarts.EChartsOption;

  legendItems: { color: string; label: string }[] = [];
  chartData: MapDistributionDataViewDto[] = [];

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    public plantationService: PlantationService,
    private mixpanelTracker: MixpanelTrackerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.['data'] ||
      changes?.['selectedMapType'] ||
      changes?.['selectedDay'] ||
      changes?.['cropHealthData'] ||
      changes?.['loading']
    ) {
      this.makeChart();
    }
  }

  ngOnInit(): void {}

  get showDistributionTabs(): boolean {
    const key = this.selectedMapType?.key;
    return key === 'ndvi' || key === 'risk';
  }

  onTabClick(tab: 'relative' | 'absolute' = 'relative') {
    if (this.selectedMapType?.labelAbreviation) {
      this.mixpanelTracker.trackServicesDistributionChartViewed({
        'map type': this.selectedMapType.labelAbreviation,
        'distribution type': tab === 'relative' ? 'Relative' : 'Absolute',
      });
    }
    this.activeTab = tab;
    this.makeChart();
  }

  makeChart() {
    this.chartData = [];
    this.legendItems = [];
    this.barChartOption = null;

    if (this.selectedDay?.isCloudly) {
      this.emitNoAnalysisIssue();
      return;
    }

    /** mapDistributionType
     * 0: NdviRelativeDistribution
     * 1: NdviAbsoluteDistribution
     * 2: InspectionDistribution
     * 3: NdmiDistribution
     */

    let mapDistributionType: number;
    if (this.selectedMapType?.key === 'ndmi') {
      mapDistributionType = 3;
    } else if (this.selectedMapType?.key === 'inspection') {
      mapDistributionType = 2;
    } else if (this.selectedMapType?.key === 'ndvi' || this.selectedMapType?.key === 'risk') {
      mapDistributionType = this.activeTab === 'relative' ? 0 : 1;
    }

    this.chartData = this.data
      ?.find((e) => e.mapDistributionType === mapDistributionType)
      ?.mapDistributionData.sort((a, b) => a.min - b.min);
    if (!this.chartData || this.chartData.length === 0) {
      this.emitNoAnalysisIssue();
      return;
    }

    if (this.chartData?.length >= 3) {
      if (this.selectedMapType?.key !== 'inspection') {
        this.legendItems = [
          {
            color: this.chartData[0].colorHex,
            label: 'doktar_app.common.very_low',
          },
          {
            color: this.chartData[1].colorHex,
            label: 'doktar_app.common.low',
          },
          {
            color: this.chartData[2].colorHex,
            label: 'doktar_app.services.crop_nutrition.soil_analysis.normal',
          },
          {
            color: this.chartData[3].colorHex,
            label: 'doktar_app.common.high',
          },
        ];
      } else {
        // for Inspection map
        this.legendItems = [
          {
            color: this.chartData[0].colorHex,
            label: 'doktar_app.common.low',
          },
          {
            color: this.chartData[1].colorHex,
            label: 'doktar_app.services.crop_nutrition.soil_analysis.normal',
          },
          {
            color: this.chartData[2].colorHex,
            label: 'doktar_app.common.high',
          },
        ];
      }

      const xAxisData: any[] = [];
      for (const item of this.chartData) {
        xAxisData.push((+item.min).toFixed(2) + (this.selectedMapType?.key === 'inspection' ? '          ' : '    ') + (+item.max).toFixed(2));
      }

      this.barChartOption = {
        grid: {
          left: '10px',
          right: '10px',
          top: '50px',
          bottom: '50px',
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#f0f0f0',
              borderColor: '#bbb',
              borderWidth: 1,
              color: '#000',
            },
          },
          formatter: (params: any) => {
            const index = params[0].dataIndex;
            const minVal = this.chartData[index].min;
            const maxVal = this.chartData[index].max;
            const avgVal = this.chartData[index].value;

            return `
          ${this.translateService.instant('doktar_app.common.min')}: ${minVal.toFixed(2)}<br/>
          ${this.translateService.instant('doktar_app.common.max')}: ${maxVal.toFixed(2)}<br/>
          ${this.translateService.instant('doktar_app.common.units.area')}: ${avgVal.toFixed(2)}
        `;
          },
        },
        legend: {
          show: false, // Hides the caption or legend at the bottom
        },
        toolbox: {
          show: false,
        },
        xAxis: [
          {
            type: 'category',
            axisTick: { show: false },
            data: xAxisData,
            axisLabel: {
              color: '#000', // Change the color of x-axis labels
              fontSize: 10, // Adjust font size (optional),
              align: 'left',
              fontWeight: 'lighter',
              padding: [0, 0, 0, this.selectedMapType?.key === 'inspection' ? -40 : -30], // pushes label to the left (adjust as needed)
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: '#E2E2E2', // Set the color of the X-axis line
                width: 1, // Optionally, adjust the line width
              },
            },
          },
        ],
        yAxis: [
          {
            show: false,
          },
        ],
        series: [
          {
            name: '',
            type: 'bar',
            barGap: 0,
            barWidth: this.selectedMapType?.key === 'inspection' ? 80 : 50,
            label: {
              show: true,
              position: 'top',
              fontSize: 12,
              formatter: '{c} ' + this.chartData[0].abbrevation,
              fontWeight: 'lighter',
            },
            itemStyle: {
              borderRadius: [10, 10, 0, 0], // Top-left, top-right, bottom-right, bottom-left
              borderColor: '#eee',
              color: (params) => {
                // Set the color based on the current index in the data array
                return this.chartData[params.dataIndex]?.colorHex;
              },
            },
            data: this.chartData.map((item: any) => item.value.toFixed(2)), // Use the value from the yAxisData array
          },
        ],
      };
    }

    this.emitNoAnalysisIssue();
  }

  // Mixpanel: detect "No Analysis" with the issue subtitle (not cloudy, not preparing).
  private getNoAnalysisIssueVisible(): boolean {
    const isPreparing = !(this.cropHealthData?.mapList?.length > 0);
    const noAnalysisShown = !(this.chartData?.length > 0);
    return !this.loading && !this.selectedDay?.isCloudly && noAnalysisShown && !isPreparing;
  }

  // Mixpanel: emit per-day state so Services Map Viewed can include it.
  private emitNoAnalysisIssue(): void {
    const date = this.selectedDay?.imageDate?.toFormat?.('yyyy-MM-dd');
    if (!date) return;
    this.noAnalysisIssueChanged.emit({ date, visible: this.getNoAnalysisIssueVisible() });
  }

  openColorLegend() {
    if (this.selectedMapType?.labelAbreviation) {
      this.mixpanelTracker.trackServicesDistributionInformationModalOpened({
        'map type': this.selectedMapType.labelAbreviation,
      });
    }
    this.dialogService.open(NdviNdmiDistributionColorLegendModalComponent, {
      header: this.translateService.instant('doktar_app.services.crop_monitoring.colors_in_legend'),
      closable: true,
      styleClass: 'no-overflow',
      modal: true, // ✅ ensures backdrop is shown
      dismissableMask: true, // Optional: allows clicking outside to close
      inputValues: {
        mapTypeKey: this.selectedMapType?.key ?? 'ndvi',
        legendColors: this.chartData?.map((item) => item.colorHex) ?? [],
      },
    });
  }
}
